import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, X, Users, Calendar, BookOpen, FileText, Eye, EyeOff, Archive, BookMarked, ExternalLink, Send, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin`;
const auth = () => ({ Authorization: `Bearer ${adminAuth.getToken()}` });

/**
 * Trilogy programs whose modules[] and price_usd fields in
 * `coaching_programs` are DISCONNECTED from the actual client-facing
 * experience. Curriculum lives in the corresponding Python constants
 * file; pricing lives in `coaching/square_catalog.py`. Editing these
 * fields via the CMS silently does nothing for a paying client — so we
 * lock them here to prevent misleading edits.
 *
 * Keep this list narrow: only the three programs that are truly wired
 * into hardcoded curriculum + Square catalog. New programs added to
 * `coaching_programs` (e.g., the-aligned-empire, drafts) must stay
 * fully editable.
 */
const TRILOGY_LOCKS = {
  "the-me-i-see": {
    curriculum_file: "coaching/me_i_see_curriculum.py",
    display_name: "The Me I See",
  },
  "healing-what-you-cannot-feel": {
    curriculum_file: "coaching/hwycf_curriculum.py",
    display_name: "Healing What You Cannot Feel",
  },
  "she-is": {
    curriculum_file: "coaching/she_is_curriculum.py",
    display_name: "She Is",
  },
};

function trilogyLockFor(slug) {
  return (slug && TRILOGY_LOCKS[slug]) || null;
}

// Even if a user re-enables the disabled inputs via devtools, strip the
// locked fields from the outbound PATCH so persisted values on the
// coaching_programs record aren't overwritten from the CMS.
function stripTrilogyLockedFields(form) {
  // Purposefully omit `price_usd`, `payment_options`. Modules are
  // managed via a separate endpoint we also gate below.
  const clone = { ...form };
  delete clone.price_usd;
  delete clone.payment_options;
  return clone;
}

function TrilogyLockedBanner({ lock, slug, testid }) {
  const [source, setSource] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (source) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/programs/by-slug/${slug}/source-of-truth`,
        { headers: auth() });
      setSource(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not load source of truth");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
         data-testid={testid || "trilogy-lock-banner"}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-medium">Content and pricing are locked for {lock.display_name}.</div>
          <div className="mt-1 text-amber-800/90 leading-relaxed">
            Curriculum modules and prices for this program are managed in
            code (<code className="text-[12px]">{lock.curriculum_file}</code> and
            <code className="text-[12px]"> coaching/square_catalog.py</code>),
            not through this CMS. Editing here only affects the public
            marketing preview at <code className="text-[12px]">/coaching</code>.
          </div>
          {slug && (
            <button onClick={toggle}
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-amber-900 hover:text-amber-950 underline"
                    data-testid="trilogy-source-of-truth-toggle">
              {loading ? "Loading…" : open ? "Hide source of truth" : "View source of truth ↗"}
            </button>
          )}
        </div>
      </div>
      {open && source && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-white/70 p-3 text-[12px]"
             data-testid="trilogy-source-of-truth-panel">
          <div className="text-amber-900/80 mb-2">
            <b>{source.total_modules} modules</b> · Pay in Full <b>${source.pricing.full_usd?.toFixed(2)}</b> · 3× Biweekly
            <b> {source.pricing.installment_usd?.map(v => `$${v.toFixed(2)}`).join(" / ")}</b>
          </div>
          <ol className="list-decimal ml-5 space-y-0.5 text-amber-950">
            {source.modules.map((m) => (
              <li key={m.module_number}>
                <b>{m.title}</b>
                {m.objective && <span className="text-amber-900/70"> — {m.objective}</span>}
                <span className="text-amber-800/60 ml-2">
                  {m.worksheet_count}w · {m.journal_count}j{m.activity_present ? " · +activity" : ""}
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-2 text-[11px] text-amber-800/70">
            Source: <code>{source.curriculum_source_file}</code> · pricing from <code>{source.pricing_source_file}</code>
          </div>
        </div>
      )}
    </div>
  );
}

const URL_TO_TAB = {
  "/admin/coaching/clients": "clients",
  "/admin/coaching/sessions": "sessions",
  "/admin/coaching/programs": "programs",
  "/admin/coaching/enrollments": "enrollments",
  "/admin/coaching/identity": "identity",
};
const TAB_TO_URL = Object.fromEntries(Object.entries(URL_TO_TAB).map(([k, v]) => [v, k]));
const TAB_TITLES = { clients: "Coaching Clients", sessions: "Coaching Sessions", programs: "Coaching Programs", enrollments: "Pending Enrollments", identity: "Identity Assessments" };

export default function AdminCoaching() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(URL_TO_TAB[location.pathname] || "clients");
  // Sync tab state whenever the URL changes (sidebar SPA nav does NOT remount
  // this component since /admin/coaching/{clients,sessions,programs,identity}
  // all render <AdminCoaching />). Without this effect, clicking sidebar
  // Links changes the URL but leaves the visible tab stuck on whatever was
  // active at first mount.
  useEffect(() => {
    const next = URL_TO_TAB[location.pathname];
    if (next && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [summary, setSummary] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [identity, setIdentity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, c, ss] = await Promise.all([
        axios.get(`${API}/summary`, { headers: auth() }),
        axios.get(`${API}/programs`, { headers: auth() }),
        axios.get(`${API}/clients`, { headers: auth() }),
        axios.get(`${API}/sessions`, { headers: auth() }),
      ]);
      setSummary(s.data); setPrograms(p.data?.programs || []);
      setClients(c.data?.items || []); setSessions(ss.data?.items || []);
      // Identity assessments come from the existing identity module (separate API)
      try {
        const idRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/identity/admin/list`, { headers: auth() });
        setIdentity(idRes.data?.items || []);
      } catch { setIdentity([]); }
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login?next=/admin/coaching");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNewClient = () => { setForm({ email: "", name: "", program_key: programs[0]?.slug || "", status: "intake", kind: "client" }); setEditing("new-client"); };
  const startEditClient = (c) => setEditing(c.id) || setForm({ ...c, kind: "client" });
  const startNewSession = () => { setForm({ client_email: "", program_key: programs[0]?.slug || "", scheduled_at: "", duration_min: 60, focus: "", status: "scheduled", kind: "session" }); setEditing("new-session"); };
  const startEditSession = (s) => setEditing(s.id) || setForm({ ...s, kind: "session" });

  const save = async () => {
    setSaving(true);
    try {
      const isSession = form.kind === "session";
      const endpoint = isSession ? `${API}/sessions` : `${API}/clients`;
      const payload = { ...form }; delete payload.kind;
      if (editing.startsWith("new-")) await axios.post(endpoint, payload, { headers: auth() });
      else await axios.patch(`${endpoint}/${editing}`, payload, { headers: auth() });
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (kind, id) => {
    if (!confirm("Delete?")) return;
    try { await axios.delete(`${API}/${kind}/${id}`, { headers: auth() }); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <AdminShell door="coaching" title={TAB_TITLES[tab] || "Coaching Hub"}>
      <div data-testid="admin-coaching-page">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Tile icon={Users} label="Clients" value={summary.clients} sub={`${summary.clients_active || 0} active`} />
            <Tile icon={Calendar} label="Sessions" value={summary.sessions} sub={`${summary.sessions_completed || 0} completed`} />
            <Tile icon={BookOpen} label="Programs" value={programs.length} sub="The Me I See / Healing / She Is" />
            <Tile icon={FileText} label="Identity Submissions" value={summary.identity_submissions} sub="Identity Mirror leads" />
            <Tile icon={Users} label="By Program" value="—" sub={Object.entries(summary.by_program || {}).map(([k, v]) => `${k.split("-").map(w=>w[0]).join("").toUpperCase()}: ${v}`).join(" · ") || "—"} />
          </div>
        )}

        <div className="border-b border-slate-200 mb-4 flex gap-4 text-sm">
          {[["clients", "Clients"], ["sessions", "Sessions"], ["programs", "Programs"], ["enrollments", "Pending Enrollments"], ["identity", "Identity Assessments"]].map(([k, l]) => (
            <button key={k} onClick={() => { setTab(k); navigate(TAB_TO_URL[k], { replace: true }); }} data-testid={`coaching-tab-${k}`} className={`py-2 ${tab === k ? "border-b-2 border-purple-900 text-purple-900 font-medium" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>

        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            {tab === "clients" && (
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-2 flex justify-end"><button onClick={startNewClient} className="rounded-xl bg-purple-900 text-white h-8 px-3 text-xs inline-flex items-center gap-1" data-testid="coaching-add-client"><Plus className="w-3 h-3" />Add Client</button></div>
                <table className="w-full text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Program</th><th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Actions</th></tr></thead>
                  <tbody>
                    {clients.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No clients yet.</td></tr>}
                    {clients.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100"><td className="px-4 py-2 font-medium">{c.name}</td><td className="px-4 py-2 text-slate-600">{c.email}</td><td className="px-4 py-2 text-xs">{c.program_key}</td><td className="px-4 py-2 text-xs">{c.status}</td><td className="px-4 py-2 text-right"><button onClick={() => startEditClient(c)} className="text-purple-900 mr-3"><Pencil className="w-3.5 h-3.5" /></button><button onClick={() => del("clients", c.id)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tab === "sessions" && (
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-2 flex justify-end"><button onClick={startNewSession} className="rounded-xl bg-purple-900 text-white h-8 px-3 text-xs inline-flex items-center gap-1" data-testid="coaching-add-session"><Plus className="w-3 h-3" />Schedule Session</button></div>
                <table className="w-full text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Client</th><th className="text-left px-4 py-2">Program</th><th className="text-left px-4 py-2">Focus</th><th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Actions</th></tr></thead>
                  <tbody>
                    {sessions.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No sessions yet.</td></tr>}
                    {sessions.map((s) => (
                      <tr key={s.id} className="border-t border-slate-100"><td className="px-4 py-2 text-xs">{(s.scheduled_at || "").slice(0, 16).replace("T", " ")}</td><td className="px-4 py-2 text-slate-600">{s.client_email}</td><td className="px-4 py-2 text-xs">{s.program_key}</td><td className="px-4 py-2 text-xs">{s.focus}</td><td className="px-4 py-2 text-xs">{s.status}</td><td className="px-4 py-2 text-right"><button onClick={() => startEditSession(s)} className="text-purple-900 mr-3"><Pencil className="w-3.5 h-3.5" /></button><button onClick={() => del("sessions", s.id)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tab === "programs" && (
              <ProgramsTab programs={programs} reload={load} summary={summary} />
            )}
            {tab === "enrollments" && (
              <PendingEnrollmentsTab />
            )}
            {tab === "identity" && (
              <div className="rounded-2xl bg-white border border-slate-200 p-4">
                <div className="text-xs text-slate-500 mb-3">Identity Mirror assessments captured from <code>/identity</code>. View full submissions at <Link to="/admin/identity" className="text-purple-900 underline">/admin/identity</Link>.</div>
                <div className="text-3xl font-display text-purple-900">{summary?.identity_submissions ?? "—"} <span className="text-sm text-slate-500">submissions</span></div>
              </div>
            )}
          </>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="coaching-form">
              <div className="flex items-center justify-between mb-4"><div className="font-display text-xl">{editing.startsWith("new-") ? "Add " + form.kind : "Edit " + form.kind}</div><button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button></div>
              {form.kind === "client" ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <label className="col-span-2"><span className="text-xs text-slate-600">Name</span><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="client-name" /></label>
                  <label className="col-span-2"><span className="text-xs text-slate-600">Email</span><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="client-email" /></label>
                  <label><span className="text-xs text-slate-600">Program</span><select value={form.program_key || ""} onChange={(e) => setForm({ ...form, program_key: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">{programs.map(p => <option key={p.slug} value={p.slug}>{p.title}</option>)}</select></label>
                  <label><span className="text-xs text-slate-600">Status</span><select value={form.status || "intake"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">{["intake", "active", "paused", "completed"].map(s => <option key={s}>{s}</option>)}</select></label>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <label className="col-span-2"><span className="text-xs text-slate-600">Client Email</span><input value={form.client_email || ""} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                  <label><span className="text-xs text-slate-600">Program</span><select value={form.program_key || ""} onChange={(e) => setForm({ ...form, program_key: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">{programs.map(p => <option key={p.slug} value={p.slug}>{p.title}</option>)}</select></label>
                  <label><span className="text-xs text-slate-600">Status</span><select value={form.status || "scheduled"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">{["scheduled", "completed", "cancelled"].map(s => <option key={s}>{s}</option>)}</select></label>
                  <label className="col-span-2"><span className="text-xs text-slate-600">Scheduled At</span><input type="datetime-local" value={(form.scheduled_at || "").slice(0, 16)} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                  <label><span className="text-xs text-slate-600">Duration (min)</span><input type="number" value={form.duration_min || 60} onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                  <label className="col-span-2"><span className="text-xs text-slate-600">Focus</span><input value={form.focus || ""} onChange={(e) => setForm({ ...form, focus: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                  <label className="col-span-2"><span className="text-xs text-slate-600">Notes</span><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full border rounded-lg p-2 mt-0.5" /></label>
                  <label className="col-span-2"><span className="text-xs text-slate-600">Next Steps</span><input value={form.next_steps || ""} onChange={(e) => setForm({ ...form, next_steps: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                </div>
              )}
              <div className="mt-6 flex justify-end gap-2"><button onClick={() => setEditing(null)} className="rounded-xl border h-9 px-3 text-sm">Cancel</button><button onClick={save} disabled={saving} className="rounded-xl bg-purple-900 text-white h-9 px-4 text-sm disabled:opacity-50" data-testid="coaching-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</button></div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-display text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1 truncate">{sub}</div>}
    </div>
  );
}


// ─────────────────────────── Programs Tab (full manager) ──────────────────────
function PriceInvariantBadge() {
  const [state, setState] = useState({ status: "loading" });
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/programs/price-invariant`,
          { headers: auth() },
        );
        setState({ status: r.data.ok ? "ok" : "drift", report: r.data });
      } catch (e) {
        setState({ status: "error", error: e?.response?.data?.detail || e.message });
      }
    })();
  }, []);
  if (state.status === "loading") {
    return <div className="text-[11px] text-slate-500" data-testid="price-invariant-loading">Checking price invariant…</div>;
  }
  if (state.status === "ok") {
    return (
      <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 inline-flex items-center gap-1.5"
           data-testid="price-invariant-ok"
           title={`checked ${state.report.checked_at}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
        Trilogy pricing matches checkout (${state.report.items.map(i => i.cms_price_usd).join(" / ")})
      </div>
    );
  }
  const drifts = (state.report?.items || []).filter(i => !i.ok);
  return (
    <div className="text-[12px] text-rose-900 bg-rose-50 border border-rose-300 rounded-xl px-3 py-2"
         data-testid="price-invariant-drift">
      <div className="font-semibold mb-1 inline-flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        Trilogy pricing drift detected — app will refuse to boot on next restart.
      </div>
      <ul className="ml-4 list-disc space-y-0.5">
        {drifts.map(d => (
          <li key={d.slug}>
            <code>{d.slug}</code>: CMS <b>{d.cms_cents ? `$${(d.cms_cents/100).toFixed(2)}` : d.reason}</b>
            {" "}vs checkout <b>${(d.checkout_cents/100).toFixed(2)}</b>
            {d.reason === "price_drift" && d.delta_cents != null && (
              <span className="text-rose-700"> (Δ {d.delta_cents > 0 ? "+" : ""}{d.delta_cents}¢)</span>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-1 text-[11px] text-rose-800/80">
        Fix by editing <code>COACHING_PRICING</code> in <code>coaching/square_catalog.py</code> or by
        restoring the correct <code>price_usd</code> in Mongo — then restart the backend.
      </div>
    </div>
  );
}


function ProgramsTab({ programs, reload, summary }) {
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const PROG_API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/programs`;

  const togglePublish = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    await axios.patch(`${PROG_API}/${p.id}`, { status: next }, { headers: auth() });
    toast.success(`${p.title} → ${next}`); reload();
  };
  const archive = async (p) => {
    if (!confirm(`Archive ${p.title}?`)) return;
    await axios.delete(`${PROG_API}/${p.id}`, { headers: auth() });
    toast.success("Archived"); reload();
  };

  return (
    <div data-testid="coaching-programs-tab">
      <div className="mb-4"><PriceInvariantBadge /></div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-sm text-slate-600">{programs.length} programs · public read at <code className="text-xs">GET /api/coaching/programs/public</code></div>
        <div className="flex items-center gap-2">
          <EmailTestSend />
          <button onClick={() => setEditing("new")} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="program-add">
            <Plus className="w-4 h-4" />Add Program
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {programs.map((p) => (
          <div key={p.id} className={`rounded-2xl border p-5 ${p.status === "published" ? "bg-white border-emerald-200" : p.status === "archived" ? "bg-slate-50 border-slate-200 opacity-70" : "bg-amber-50/40 border-amber-200"}`} data-testid={`program-card-${p.slug}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wide text-purple-900">{p.program_type || "program"}</div>
                <div className="font-display text-lg text-slate-900 truncate">{p.title}{p.trademark && <sup className="text-xs ml-0.5">™</sup>}</div>
                <div className="text-[10px] text-slate-500 font-mono">/{p.slug}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-100 text-emerald-800" : p.status === "archived" ? "bg-slate-200 text-slate-700" : "bg-amber-200 text-amber-900"}`}>{p.status}</span>
            </div>
            <div className="text-xs text-slate-600 mt-3 line-clamp-2">{p.description}</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-slate-600">
              <div><span className="text-slate-400">Duration</span><br/>{p.duration_weeks} wks</div>
              <div><span className="text-slate-400">Price</span><br/>${(p.price_usd || 0).toLocaleString()}</div>
              <div><span className="text-slate-400">Clients</span><br/>{summary?.by_program?.[p.slug] || 0}</div>
            </div>
            <div className="mt-3 text-[11px] text-slate-500">{(p.modules || []).length} modules · {(p.modules || []).reduce((n,m) => n + (m.lessons || []).length, 0)} lessons</div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <button onClick={() => togglePublish(p)} className={p.status === "published" ? "text-emerald-700" : "text-amber-700"} title="Publish toggle" data-testid={`program-publish-${p.slug}`}>
                  {p.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                {p.featured && <span className="text-[10px] text-purple-900 uppercase">Featured</span>}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setManaging(p)} className="text-purple-900 inline-flex items-center gap-1 text-xs" data-testid={`program-modules-${p.slug}`}><BookMarked className="w-3.5 h-3.5" />Manage</button>
                <button onClick={() => setEditing(p)} className="text-purple-900" data-testid={`program-edit-${p.slug}`}><Pencil className="w-3.5 h-3.5" /></button>
                {p.status !== "archived" && <button onClick={() => archive(p)} className="text-rose-600" data-testid={`program-archive-${p.slug}`}><Archive className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <ProgramFormModal program={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {managing && <ModulesModal program={managing} onClose={() => setManaging(null)} onChanged={reload} />}
    </div>
  );
}

function ProgramFormModal({ program, onClose, onSaved }) {
  const [form, setForm] = useState(program || {
    slug: "", title: "", description: "", target_audience: "", program_type: "cohort",
    duration_weeks: 12, price_usd: 0, payment_options: ["paid_in_full"], status: "draft",
    featured: false, trademark: true, coaching_cta_url: "", square_payment_url: "",
  });
  const [saving, setSaving] = useState(false);
  const PROG_API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/programs`;
  const lock = trilogyLockFor(program?.slug);

  const save = async () => {
    setSaving(true);
    try {
      // Trilogy safety: even if someone modifies the DOM to re-enable
      // the disabled inputs, strip the locked fields from the PATCH body
      // so we never overwrite persisted content/pricing values.
      const payload = lock ? stripTrilogyLockedFields(form) : form;
      if (program?.id) {
        await axios.patch(`${PROG_API}/${program.id}`, payload, { headers: auth() });
      } else {
        await axios.post(PROG_API, payload, { headers: auth() });
      }
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="program-form">
        <div className="flex items-center justify-between mb-4"><div className="font-display text-xl">{program ? "Edit Program" : "Add Program"}</div><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        {lock && <TrilogyLockedBanner lock={lock} slug={program?.slug} testid="trilogy-lock-banner" />}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label><span className="text-xs text-slate-600">Title</span><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="program-title" /></label>
          <label><span className="text-xs text-slate-600">Slug</span><input value={form.slug} disabled={!!program} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5 disabled:bg-slate-100" data-testid="program-slug" /></label>
          <label className="col-span-2"><span className="text-xs text-slate-600">Description</span><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full border rounded-lg p-2 mt-0.5" /></label>
          <label className="col-span-2"><span className="text-xs text-slate-600">Target Audience</span><input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
          <label><span className="text-xs text-slate-600">Type</span><select value={form.program_type} onChange={(e) => setForm({ ...form, program_type: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">{["cohort", "intensive", "mastermind", "self_paced"].map(s => <option key={s}>{s}</option>)}</select></label>
          <label><span className="text-xs text-slate-600">Duration (weeks)</span><input type="number" value={form.duration_weeks} onChange={(e) => setForm({ ...form, duration_weeks: Number(e.target.value) })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
          <label className={lock ? "opacity-50" : ""}>
            <span className="text-xs text-slate-600">Price (USD){lock && " · locked"}</span>
            <input type="number" value={form.price_usd}
                    disabled={!!lock}
                    onChange={(e) => setForm({ ...form, price_usd: Number(e.target.value) })}
                    className="w-full border rounded-lg h-9 px-2 mt-0.5 disabled:bg-slate-100 disabled:cursor-not-allowed"
                    data-testid="program-price" />
          </label>
          <label><span className="text-xs text-slate-600">Status</span><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="program-status">{["draft", "published", "archived"].map(s => <option key={s}>{s}</option>)}</select></label>
          <label className={`col-span-2 ${lock ? "opacity-50" : ""}`}>
            <span className="text-xs text-slate-600">Payment options (comma-separated){lock && " · locked"}</span>
            <input value={(form.payment_options || []).join(", ")}
                    disabled={!!lock}
                    onChange={(e) => setForm({ ...form, payment_options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    className="w-full border rounded-lg h-9 px-2 mt-0.5 disabled:bg-slate-100 disabled:cursor-not-allowed"
                    data-testid="program-payment-options" />
          </label>
          <label className="col-span-2"><span className="text-xs text-slate-600">Coaching CTA URL (Calendly etc.)</span><input value={form.coaching_cta_url} onChange={(e) => setForm({ ...form, coaching_cta_url: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
          <label className="col-span-2"><span className="text-xs text-slate-600">Square Payment URL (optional)</span><input value={form.square_payment_url} onChange={(e) => setForm({ ...form, square_payment_url: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
          <div className="col-span-2 flex items-center gap-4 text-xs">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />Featured</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.trademark} onChange={(e) => setForm({ ...form, trademark: e.target.checked })} />Trademark</label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border h-9 px-3 text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.title || !form.slug} className="rounded-xl bg-purple-900 text-white h-9 px-4 text-sm disabled:opacity-50" data-testid="program-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function ModulesModal({ program, onClose, onChanged }) {
  const [p, setP] = useState(program);
  const [newModule, setNewModule] = useState({ title: "", order: (program.modules || []).length + 1, summary: "" });
  const [expanded, setExpanded] = useState(null);
  const PROG_API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/programs`;
  const lock = trilogyLockFor(p.slug);

  const refresh = async () => {
    const r = await axios.get(`${PROG_API}/${p.id}`, { headers: auth() });
    setP(r.data.program); onChanged();
  };
  const addModule = async () => {
    if (lock) return; // belt-and-braces — button is also disabled
    if (!newModule.title.trim()) return;
    await axios.post(`${PROG_API}/${p.id}/modules`, newModule, { headers: auth() });
    setNewModule({ title: "", order: (p.modules || []).length + 2, summary: "" });
    refresh();
  };
  const delModule = async (mid) => {
    if (lock) return;
    if (!confirm("Delete module + lessons?")) return;
    await axios.delete(`${PROG_API}/${p.id}/modules/${mid}`, { headers: auth() }); refresh();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="modules-modal">
        <div className="flex items-center justify-between mb-4">
          <div><div className="font-display text-xl">{p.title}</div><div className="text-xs text-slate-500">Modules · Lessons · Worksheets · Journal Prompts · Activities</div></div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        {lock && <TrilogyLockedBanner lock={lock} slug={p.slug} testid="trilogy-lock-modules-banner" />}

        <div className={`rounded-xl border border-slate-200 p-3 bg-slate-50 mb-4 ${lock ? "opacity-50" : ""}`}
             data-testid="add-module-panel"
             aria-disabled={lock ? "true" : "false"}>
          <div className="text-xs uppercase tracking-wide text-slate-600 mb-2">
            Add Module{lock && " · locked"}
          </div>
          <div className="grid grid-cols-12 gap-2">
            <input placeholder="Title" value={newModule.title}
                    disabled={!!lock}
                    onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                    className="col-span-5 border rounded-lg h-9 px-2 text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                    data-testid="module-title" />
            <input placeholder="Summary" value={newModule.summary}
                    disabled={!!lock}
                    onChange={(e) => setNewModule({ ...newModule, summary: e.target.value })}
                    className="col-span-5 border rounded-lg h-9 px-2 text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
            <input type="number" value={newModule.order}
                    disabled={!!lock}
                    onChange={(e) => setNewModule({ ...newModule, order: Number(e.target.value) })}
                    className="col-span-1 border rounded-lg h-9 px-2 text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
            <button onClick={addModule}
                    disabled={!!lock}
                    className="col-span-1 bg-purple-900 text-white rounded-lg h-9 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="module-add">Add</button>
          </div>
        </div>

        <div className="space-y-3">
          {(p.modules || []).length === 0 && <div className="text-center text-slate-400 text-sm py-6">No modules yet.</div>}
          {(p.modules || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((m) => (
            <div key={m.id} className="rounded-xl border border-slate-200 p-3" data-testid={`module-${m.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-slate-500">Module #{m.order}</div>
                  <div className="font-medium">{m.title}</div>
                  {m.summary && <div className="text-xs text-slate-600">{m.summary}</div>}
                  <div className="text-[10px] text-slate-500 mt-1">{(m.lessons || []).length} lessons</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setExpanded(expanded === m.id ? null : m.id)} className="text-xs text-purple-900 underline">{expanded === m.id ? "Hide" : "Open"}</button>
                  {!lock && (
                    <button onClick={() => delModule(m.id)} className="text-rose-600" data-testid={`module-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
              {expanded === m.id && (
                <LessonEditor programId={p.id} moduleId={m.id} lessons={m.lessons || []} onChanged={refresh} readOnly={!!lock} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 text-right">
          <a href={`${process.env.REACT_APP_BACKEND_URL.replace("/api", "")}/coaching/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />Preview public page</a>
        </div>
      </div>
    </div>
  );
}

function LessonEditor({ programId, moduleId, lessons, onChanged, readOnly = false }) {
  const [newLesson, setNewLesson] = useState({ title: "", order: lessons.length + 1, content_md: "", video_url: "" });
  const PROG_API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/programs`;
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const add = async () => {
    if (readOnly) return;
    if (!newLesson.title.trim()) return;
    await axios.post(`${PROG_API}/${programId}/modules/${moduleId}/lessons`, newLesson, { headers: auth() });
    setNewLesson({ title: "", order: lessons.length + 2, content_md: "", video_url: "" }); onChanged();
  };
  const save = async (overrides = {}) => {
    if (readOnly) return;
    const payload = { ...form, ...overrides };
    delete payload.worksheets_str; delete payload.prompts_str; delete payload.activities_str;
    await axios.patch(`${PROG_API}/${programId}/modules/${moduleId}/lessons/${editing}`, payload, { headers: auth() });
    setEditing(null); onChanged();
  };
  const del = async (lid) => {
    if (readOnly) return;
    if (!confirm("Delete lesson?")) return;
    await axios.delete(`${PROG_API}/${programId}/modules/${moduleId}/lessons/${lid}`, { headers: auth() }); onChanged();
  };

  return (
    <div className="mt-3 pl-3 border-l-2 border-purple-100">
      <div className={`bg-slate-50 rounded-lg p-2 mb-2 ${readOnly ? "opacity-50" : ""}`}>
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
          Add lesson{readOnly && " · locked"}
        </div>
        <div className="grid grid-cols-12 gap-2">
          <input placeholder="Title" value={newLesson.title} disabled={readOnly} onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })} className="col-span-5 border rounded h-8 px-2 text-xs disabled:bg-slate-100 disabled:cursor-not-allowed" data-testid="lesson-title" />
          <input placeholder="Video URL (Vimeo/YouTube/Loom)" value={newLesson.video_url} disabled={readOnly} onChange={(e) => setNewLesson({ ...newLesson, video_url: e.target.value })} className="col-span-5 border rounded h-8 px-2 text-xs disabled:bg-slate-100 disabled:cursor-not-allowed" />
          <input type="number" value={newLesson.order} disabled={readOnly} onChange={(e) => setNewLesson({ ...newLesson, order: Number(e.target.value) })} className="col-span-1 border rounded h-8 px-2 text-xs disabled:bg-slate-100 disabled:cursor-not-allowed" />
          <button onClick={add} disabled={readOnly} className="col-span-1 bg-purple-900 text-white rounded h-8 text-xs disabled:opacity-40 disabled:cursor-not-allowed" data-testid="lesson-add">+</button>
        </div>
      </div>
      {lessons.sort((a, b) => (a.order || 0) - (b.order || 0)).map((l) => (
        <div key={l.id} className="text-xs py-1 flex items-center justify-between" data-testid={`lesson-${l.id}`}>
          <div className="flex-1 min-w-0">
            <span className="text-slate-400">#{l.order}</span> <span className="font-medium">{l.title}</span>
            {l.video_url && <span className="text-purple-900 ml-2">▶ video</span>}
            {(l.worksheets?.length || 0) > 0 && <span className="text-slate-500 ml-2">📄 {l.worksheets.length}</span>}
            {(l.journal_prompts?.length || 0) > 0 && <span className="text-slate-500 ml-2">📝 {l.journal_prompts.length}</span>}
            {(l.activities?.length || 0) > 0 && <span className="text-slate-500 ml-2">⚡ {l.activities.length}</span>}
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2">
              <button onClick={() => { setForm({ ...l, worksheets_str: (l.worksheets || []).map(w => `${w.title}|${w.url}`).join("\n"), prompts_str: (l.journal_prompts || []).join("\n"), activities_str: (l.activities || []).map(a => `${a.title}|${a.description}`).join("\n") }); setEditing(l.id); }} className="text-purple-900"><Pencil className="w-3 h-3" /></button>
              <button onClick={() => del(l.id)} className="text-rose-600"><Trash2 className="w-3 h-3" /></button>
            </div>
          )}
        </div>
      ))}
      {editing && !readOnly && (
        <div className="mt-2 rounded-lg border border-purple-200 p-3 bg-purple-50/30">
          <div className="text-[10px] uppercase tracking-wide text-purple-900 mb-2">Edit lesson · {form.title}</div>
          <textarea placeholder="Worksheets (one per line: Title|URL)" value={form.worksheets_str || ""} onChange={(e) => setForm({ ...form, worksheets_str: e.target.value })} rows={2} className="w-full border rounded p-2 text-xs mb-2" data-testid="lesson-worksheets" />
          <textarea placeholder="Journal prompts (one per line)" value={form.prompts_str || ""} onChange={(e) => setForm({ ...form, prompts_str: e.target.value })} rows={3} className="w-full border rounded p-2 text-xs mb-2" data-testid="lesson-prompts" />
          <textarea placeholder="Activities (one per line: Title|Description)" value={form.activities_str || ""} onChange={(e) => setForm({ ...form, activities_str: e.target.value })} rows={2} className="w-full border rounded p-2 text-xs mb-2" data-testid="lesson-activities" />
          <textarea placeholder="Markdown content" value={form.content_md || ""} onChange={(e) => setForm({ ...form, content_md: e.target.value })} rows={3} className="w-full border rounded p-2 text-xs mb-2" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(null)} className="text-xs border rounded h-7 px-3">Cancel</button>
            <button onClick={() => {
              save({
                worksheets: form.worksheets_str.split("\n").map(s => { const [t, u] = s.split("|"); return t ? { title: t.trim(), url: (u || "").trim() } : null; }).filter(Boolean),
                journal_prompts: form.prompts_str.split("\n").map(s => s.trim()).filter(Boolean),
                activities: form.activities_str.split("\n").map(s => { const [t, d] = s.split("|"); return t ? { title: t.trim(), description: (d || "").trim() } : null; }).filter(Boolean),
              });
            }} className="text-xs bg-purple-900 text-white rounded h-7 px-3" data-testid="lesson-save-content">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// `auth()` helper defined at top of file is reused by all child components below.


// ─────────────────────────── Email Test Send (config diagnostic) ───────
// One-click diagnostic that pings the current Resend config with a minimal
// HTML payload to a chosen recipient. Surfaces the exact Resend response
// (email_id on success, raw error string on failure) so the admin can see
// deliverability problems without digging through backend logs. Not a
// welcome-email preview — this is strictly a health check on
// SENDER_EMAIL + RESEND_API_KEY.
function EmailTestSend() {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("delivered@resend.dev");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  async function send() {
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/email/test-send`,
        { to_email: to.trim().toLowerCase() },
        { headers: auth() }
      );
      setResult(r.data);
    } catch (e) {
      setResult({
        ok: false,
        resend_result: { error: e?.response?.data?.detail || e.message },
      });
    }
    setSending(false);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="email-test-send-btn"
        className="rounded-xl bg-white border border-purple-900 text-purple-900 h-9 px-3 text-sm inline-flex items-center gap-1.5 hover:bg-purple-50"
        title="Send a diagnostic email to verify Resend config"
      >
        <Send className="w-4 h-4" />
        Test Send
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="email-test-send-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">Email Deliverability Test</div>
              <button onClick={() => setOpen(false)}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Sends a minimal HTML email via the current <code>SENDER_EMAIL</code> and{" "}
              <code>RESEND_API_KEY</code> to verify Resend delivery. Use{" "}
              <code>delivered@resend.dev</code> for a Resend-side test with no real inbox impact.
            </p>
            <label className="block text-sm mb-3">
              <span className="text-xs text-slate-600">Recipient</span>
              <input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full border rounded-lg h-9 px-2 mt-0.5"
                data-testid="email-test-recipient"
                placeholder="you@example.com"
              />
            </label>
            <button
              onClick={send}
              disabled={sending || !to.includes("@")}
              className="rounded-xl bg-purple-900 text-white h-9 px-4 text-sm inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="email-test-send-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send diagnostic
                </>
              )}
            </button>

            {result && (
              <div
                className={`mt-4 rounded-xl border p-4 text-sm ${
                  result.ok ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"
                }`}
                data-testid="email-test-result"
              >
                <div className="flex items-center gap-2 mb-2 font-semibold">
                  {result.ok ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                      <span className="text-emerald-800">Delivered to Resend</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-rose-700" />
                      <span className="text-rose-800">Send failed</span>
                    </>
                  )}
                </div>
                <div className="text-xs space-y-1">
                  <div>
                    <span className="text-slate-500">From:</span> {result.sender_name} &lt;
                    {result.sender_email}&gt;
                  </div>
                  {result?.resend_result?.email_id && (
                    <div>
                      <span className="text-slate-500">Resend email_id:</span>{" "}
                      <code>{result.resend_result.email_id}</code>
                    </div>
                  )}
                  {result?.resend_result?.error && (
                    <div className="text-rose-800">
                      <span className="text-slate-500">Error:</span>{" "}
                      <code>{result.resend_result.error}</code>
                    </div>
                  )}
                  {result?.email_logs_row?.status && (
                    <div>
                      <span className="text-slate-500">email_logs.status:</span>{" "}
                      <code>{result.email_logs_row.status}</code>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────── Pending Enrollments Tab ───────────────────
// Lists self-serve intake submissions awaiting admin action. Admin can
// mark payment received, then approve (which converts to a real
// coaching_program_enrollments row + fires the welcome-trilogy email) or
// reject with a reason. Filter toggle between pending / approved /
// rejected lists.
function PendingEnrollmentsTab() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const base = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/admin/enrollments`;

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${base}/pending?status=${status}`, { headers: auth() });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
      setRows([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function markPaid(id) {
    setBusy(id);
    try {
      await axios.patch(`${base}/pending/${id}/mark-paid`, {}, { headers: auth() });
      toast.success("Marked paid");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(null);
  }
  async function approve(id) {
    setBusy(id);
    try {
      const r = await axios.post(`${base}/pending/${id}/approve`, {}, { headers: auth() });
      toast.success(`Enrolled · welcome email ${r.data?.welcome_email?.status || "queued"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed");
    }
    setBusy(null);
  }
  async function reject(id) {
    const reason = prompt("Rejection reason (visible to admin only)?") || "";
    setBusy(id);
    try {
      await axios.post(`${base}/pending/${id}/reject`, { reason }, { headers: auth() });
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(null);
  }

  return (
    <div data-testid="pending-enrollments-tab">
      <div className="flex items-center gap-2 mb-4 text-sm">
        {[
          ["pending", "Pending"],
          ["approved", "Approved"],
          ["rejected", "Rejected"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setStatus(k)}
            data-testid={`pending-filter-${k}`}
            className={`h-8 px-3 rounded-full text-xs ${
              status === k
                ? "bg-purple-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin inline" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            No {status} enrollments right now.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="pending-enrollments-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Prospect</th>
                <th className="text-left px-4 py-2">Program</th>
                <th className="text-left px-4 py-2">Payment</th>
                <th className="text-left px-4 py-2">Submitted</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`pending-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                    {r.phone && <div className="text-xs text-slate-500">{r.phone}</div>}
                    {r.notes && (
                      <div className="text-xs italic text-slate-500 mt-1 max-w-[280px] truncate" title={r.notes}>
                        “{r.notes}”
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-medium">{r.program_title}</div>
                    <div className="text-xs text-slate-500">
                      ${r.program_price_usd?.toLocaleString?.() || r.program_price_usd || "—"} ·{" "}
                      {r.payment_option}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{r.payment_method_label}</div>
                    {r.payment_reference && (
                      <div className="text-[11px] text-slate-500">ref: {r.payment_reference}</div>
                    )}
                    <div className="mt-1">
                      <span
                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          r.payment_status === "paid"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-slate-100 text-slate-600"
                        }`}
                        data-testid={`pending-paid-${r.id}`}
                      >
                        {r.payment_status || "unpaid"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {status === "pending" && (
                      <div className="inline-flex flex-col gap-1 items-end">
                        {r.payment_status !== "paid" && (
                          <button
                            onClick={() => markPaid(r.id)}
                            disabled={busy === r.id}
                            className="text-xs bg-white border border-slate-300 rounded-full px-3 py-1 hover:border-purple-900 hover:text-purple-900 disabled:opacity-40"
                            data-testid={`mark-paid-${r.id}`}
                          >
                            Mark paid
                          </button>
                        )}
                        <button
                          onClick={() => approve(r.id)}
                          disabled={busy === r.id || r.payment_status !== "paid"}
                          className="text-xs bg-emerald-700 text-white rounded-full px-3 py-1 disabled:opacity-40"
                          data-testid={`approve-${r.id}`}
                          title={r.payment_status !== "paid" ? "Mark paid first" : ""}
                        >
                          Approve → Enroll
                        </button>
                        <button
                          onClick={() => reject(r.id)}
                          disabled={busy === r.id}
                          className="text-xs bg-white border border-rose-300 text-rose-700 rounded-full px-3 py-1 disabled:opacity-40"
                          data-testid={`reject-${r.id}`}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {status === "approved" && (
                      <div className="text-xs text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> enrolled
                      </div>
                    )}
                    {status === "rejected" && (
                      <div className="text-xs text-rose-700" title={r.rejection_reason || ""}>
                        rejected
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

